import { Button, Color, Icon, Radius, Spacing } from 'zens-storybook'
import React, { ReactElement } from 'react'
import { Pressable, StyleProp, StyleSheet, View, ViewProps, ViewStyle } from 'react-native'

import { Href, Link } from 'zens-router'

import Card from './card'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.L,
    padding: Spacing.S,
  },
  card: {
    flex: 1,
    width: 340,
    margin: Spacing.S,
  },
  dummy: {
    marginVertical: 0,
  },
  head: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: Spacing.S,
  },
  button: {
    marginLeft: Spacing.XS,
  },
  buttonInactive: {
    borderColor: Color.calmGrey,
  },
  iconInactive: {
    color: Color.metalGrey,
  },
  hover: {
    borderWidth: 2,
    borderColor: Color.premiumBlack,
    margin: Spacing.S - 1,
    minWidth: 342,
  },
  pressed: {
    backgroundColor: Color.sleepingGrey,
  },
  noPress: {
    // @ts-expect-error Cursor only relevant for web
    cursor: 'default',
  },
  skeletonButton: {
    minWidth: 0,
  },
})

export interface GridItemInfo<T> {
  item: GridItem<T>
  index: number
}

interface GridItem<T> {
  data: T
  onPress?: () => void
  href?: Href
}

export interface Props<T> extends ViewProps {
  items: GridItem<T>[]
  renderItem: (info: GridItemInfo<T>) => ReactElement | null
  keyExtractor?: (item: T, index: number) => string
  typeHref?: (type: 'table' | 'grid') => Href
  contentStyle?: StyleProp<ViewStyle>
  testOnly_pressed?: boolean
}
function Grid<T = any>(props: Props<T>) {
  const { items, renderItem, keyExtractor, typeHref, contentStyle, testOnly_pressed, style, ...otherProps } = props

  const renderHead = () => {
    return (
      <View style={styles.head}>
        <Link href={typeHref?.('table') || ''} asChild>
          <Button
            left={<Icon name="menu" size={18} style={styles.iconInactive} />}
            style={styles.button}
            light
            contentContainerStyle={styles.buttonInactive}
            testID="Grid.TableButton"
          />
        </Link>
        <Link href={typeHref?.('grid') || ''} asChild>
          <Button left="grid" style={styles.button} light testID="Grid.GridButton" />
        </Link>
      </View>
    )
  }

  const keyForItem = (item: T, index: number): string => {
    if (keyExtractor) return keyExtractor(item, index)
    if (item && typeof item === 'object' && 'key' in item) return `${(item as { key: any }).key}`
    return `${index}`
  }

  const renderCard = (item: GridItem<T>, index: number) => (state: { pressed: boolean; hovered?: boolean }) => {
    const { pressed, hovered } = state
    const info = { item, index }
    const style =
      item.onPress || item.href ? [styles.card, hovered && styles.hover, pressed && styles.pressed] : styles.card
    return (
      <Card style={style} testID="Grid.Card">
        {renderItem(info)}
      </Card>
    )
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      {typeHref && renderHead()}
      <View style={[styles.content, contentStyle]}>
        {items?.map((item, index) =>
          item.href ? (
            <Link href={item.href} asChild key={`link-${keyForItem(item.data, index)}`}>
              <Pressable onPress={item.onPress} testID="Grid.Pressable" testOnly_pressed={testOnly_pressed}>
                {renderCard(item, index)}
              </Pressable>
            </Link>
          ) : (
            <Pressable
              key={`pressable-${keyForItem(item.data, index)}`}
              onPress={item.onPress}
              testID="Grid.Pressable"
              style={!item.onPress && styles.noPress}
              testOnly_pressed={testOnly_pressed}
            >
              {renderCard(item, index)}
            </Pressable>
          ),
        )}
        <View style={[styles.card, styles.dummy]} />
        <View style={[styles.card, styles.dummy]} />
      </View>
    </View>
  )
}

interface SkeletonProps extends ViewProps {
  numberOfItems?: number
  itemElement?: ReactElement | null
  hasChangeView?: boolean
  contentStyle?: StyleProp<ViewStyle>
}

Grid.Skeleton = (props: SkeletonProps) => {
  const { numberOfItems = 1, itemElement, hasChangeView, contentStyle, style, ...otherProps } = props

  function renderHead() {
    return (
      <View style={styles.head}>
        <Button.Skeleton style={styles.button} contentStyle={styles.skeletonButton} />
        <Button.Skeleton style={styles.button} contentStyle={styles.skeletonButton} />
      </View>
    )
  }
  return (
    <View style={[styles.container, style]} {...otherProps}>
      {hasChangeView && renderHead()}
      <View style={[styles.content, contentStyle]}>
        {Array.from({ length: numberOfItems }).map((_, index) => (
          <View key={index}>
            <Card style={styles.card}>{itemElement}</Card>
          </View>
        ))}
        <View style={[styles.card, styles.dummy]} />
        <View style={[styles.card, styles.dummy]} />
      </View>
    </View>
  )
}

export default Grid
